/**
 * Join page visit tracking service.
 * - recordVisit(): calls Cloud Function (deduped by localStorage, once per day per browser)
 * - getVisitsForYear(year): reads monthly counters from Firestore (admin use)
 */
import { callFunction } from '@core/firebase/functions.js'
import { getFirebaseDb } from '@core/firebase/firebase.js'
import { collection, getDocs } from 'firebase/firestore'

const VISIT_KEY_PREFIX = 'joinPageVisit_'

/**
 * Record a visit to the /join page.
 * Deduplicates by localStorage — only counts once per browser per calendar day.
 * Silently fails (fire-and-forget) so it never blocks the user.
 */
export function recordVisit() {
  try {
    const today = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
    const key = `${VISIT_KEY_PREFIX}${today}`

    if (localStorage.getItem(key)) return // Already recorded today

    localStorage.setItem(key, '1')

    // Clean up old keys (keep only today)
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(VISIT_KEY_PREFIX) && k !== key) {
        localStorage.removeItem(k)
      }
    }

    // Fire and forget — don't await, don't block
    callFunction('recordJoinPageVisit', {}).catch(() => {
      // If the call fails, remove the key so it retries next page load
      localStorage.removeItem(key)
    })
  } catch (_) {
    // localStorage may be unavailable (private browsing, etc.) — ignore
  }
}

/**
 * Hebrew month names for display
 */
const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
]

/**
 * Fetch visit counts for all months of a given year.
 * @param {number} year - e.g. 2026
 * @returns {Promise<{ months: Array<{ key: string, label: string, count: number }>, total: number }>}
 */
export async function getVisitsForYear(year) {
  const db = getFirebaseDb()
  const colRef = collection(db, 'Utils', 'JoinPageVisits', 'Monthly')
  const snapshot = await getDocs(colRef)

  // Build a map of monthKey -> count from all docs matching the year
  const countsMap = {}
  snapshot.forEach((doc) => {
    const docId = doc.id // e.g. "2026-03"
    if (docId.startsWith(`${year}-`)) {
      countsMap[docId] = doc.data().count || 0
    }
  })

  // Build ordered array for all 12 months
  const months = []
  let total = 0
  for (let m = 1; m <= 12; m++) {
    const key = `${year}-${String(m).padStart(2, '0')}`
    const count = countsMap[key] || 0
    total += count
    months.push({
      key,
      label: HEBREW_MONTHS[m - 1],
      count,
    })
  }

  return { months, total }
}
